import {
  type DataTableColumn,
  type DataTableDimensionsType,
  type DataTableKpiDisplayNameType,
  type DataTableViewType,
  sortFromCompareArray
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, type Ref, unref } from 'vue';

import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import { getDataTableColumns } from '#domain/performance/kpi/kpi.query';
import { getTaxTypeName } from '#helpers';

interface UseDataTableColumnsParams {
  dataTableView: Ref<DataTableViewType>;
  selectedDimensions: Ref<DataTableDimensionsType[] | null>;
  selectedKpis: Ref<DataTableKpiDisplayNameType[] | null>;
  configurationColumnsOrder: Ref<string[]>;
  canFetchColumns: Ref<boolean>;
}

export function useDataTableColumns(params: UseDataTableColumnsParams) {
  const { dataTableView, selectedDimensions, selectedKpis, canFetchColumns, configurationColumnsOrder } = params;

  const { t } = useLanguage();

  const {
    taxonomy: { taxonomy },
    withTax: { isWithTax },
    period: { selectedPeriod }
  } = useKpisPerimeter();

  const fetchDataTableColumns = async () => {
    return getDataTableColumns({
      dataTableView: unref(dataTableView),
      dimensions: unref(selectedDimensions) ?? [],
      kpis: unref(selectedKpis) ?? [],
      taxonomy: unref(taxonomy),
      period: unref(selectedPeriod).period
    });
  };

  const queryKey = computed(() => {
    return [
      'dataTableColumns',
      dataTableView,
      selectedDimensions,
      selectedKpis.value?.sort() ?? selectedKpis,
      taxonomy,
      selectedPeriod
    ];
  });

  const { data: dataTableColumns, isFetching: isFetchingColumns } = useQuery({
    queryKey,
    queryFn: fetchDataTableColumns,
    enabled: canFetchColumns
  });

  const columnsDefinition = computed(() => {
    return (
      dataTableColumns.value?.reduce<Record<string, DataTableColumn>>(
        (acc, column) => ({
          ...acc,
          [column.name]: {
            ...column,
            label: t(column.label, { taxType: t(getTaxTypeName(isWithTax.value)) })
          }
        }),
        {}
      ) ?? {}
    );
  });

  const columnsOrder = computed(() => {
    if (!dataTableColumns.value) {
      return [];
    }

    return dataTableColumns.value
      .map((column) => column.name)
      .sort(sortFromCompareArray(configurationColumnsOrder.value));
  });

  const fixedColumns = computed(() => {
    return dataTableColumns.value?.filter((column) => column.type === 'dimension').map((column) => column.name) ?? [];
  });

  const areColumnsFetched = computed(() => !!dataTableColumns.value);

  return {
    dataTableColumns,
    columnsDefinition,
    columnsOrder,
    fixedColumns,
    areColumnsFetched,
    isFetchingColumns
  };
}
